import { Trash2 } from "lucide-react";
import { useState } from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@patient-watch/ui/alert-dialog";
import { Label } from "@patient-watch/ui/label";
import { Input } from "@patient-watch/ui/input";
import { cn } from "@/lib/utils";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useDeleteUser } from "../api/deleteUserMutation";

const DeleteUserButton = () => {
    const copy = useProfileCopy();
    const deleteUser = useDeleteUser();
    const [confirmation, setConfirmation] = useState("");
    const [open, setOpen] = useState(false);
    const isConfirmValid = confirmation.toLowerCase() === "delete my account";

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen);
        if (!newOpen) {
            setConfirmation("");
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={handleOpenChange}>
            <AlertDialogTrigger
                className={cn(
                    "flex items-center gap-x-3 my-4 w-full font-medium text-red-600 hover:text-red-700 dark:hover:text-red-600 dark:text-red-500 text-xl cursor-pointer"
                )}
            >
                <Trash2 size={32} strokeWidth={1.75} />
                {copy.deleteAccount}
            </AlertDialogTrigger>

            <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                    <AlertDialogTitle>{copy.deleteAccountConfirmTitle}</AlertDialogTitle>
                    <AlertDialogDescription>{copy.deleteAccountConfirmDescription}</AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                    <Label htmlFor="confirm-delete">{copy.deleteAccountConfirmationLabel}</Label>
                    <Input
                        id="confirm-delete"
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        placeholder={copy.deleteAccountPlaceholder}
                        className="mt-2"
                    />
                </div>
                <AlertDialogFooter className="flex md:flex-row flex-col gap-y-4">
                    <AlertDialogAction asChild>
                        <fc.DeleteButton
                            variant="destructive"
                            size="lg"
                            label={copy.deleteAccount}
                            loadingText={copy.deletingAccount}
                            isLoading={deleteUser.isPending}
                            disabled={!isConfirmValid}
                            onClick={() => deleteUser.mutate()}
                            className={cn("w-auto", !isConfirmValid && "opacity-50 cursor-not-allowed")}
                        />
                    </AlertDialogAction>
                    <AlertDialogCancel asChild>
                        <fc.CancelButton
                            variant="outline"
                            size="lg"
                            label={copy.cancel}
                            className="w-auto text-slate-800"
                        />
                    </AlertDialogCancel>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default DeleteUserButton;
